import React, { useState } from 'react';
import {
  Box, Paper, Typography, Collapse, Button, Chip, Divider, Stack,
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Terminal, CheckCircle, RemoveCircleOutline,
  AutoAwesome, Bolt,
} from '@mui/icons-material';

const MONO = { fontFamily: 'monospace', fontSize: 12 };

function JsonBlock({ value, label = 'JSON', defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (value == null) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Button
        size="small"
        sx={{ ...MONO, textTransform: 'none', minWidth: 0, p: 0.25 }}
        onClick={() => setOpen((v) => !v)}
        endIcon={open ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
      >
        {label}
      </Button>
      <Collapse in={open}>
        <Box
          component="pre"
          sx={{
            ...MONO, m: 0, mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'grey.900', color: 'grey.100',
            overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}
        >
          {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
}

function EventRow({ ev }) {
  if (ev.stage === 'deterministic') {
    if (ev.skipped) {
      const reason = ev.reason === 'strategy_off'
        ? 'déterministe coupé (generationStrategy)'
        : ev.reason === 'no_builder'
          ? `${ev.provider} — aucun builder`
          : ev.reason;
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={MONO}>
          <RemoveCircleOutline fontSize="inherit" sx={{ color: 'text.disabled' }} />
          <Typography sx={{ ...MONO, color: 'text.disabled' }}>
            {`+${ev.atMs}ms  déterministe · ${reason}`}
          </Typography>
        </Stack>
      );
    }
    const produced = ev.produced;
    return (
      <Box sx={MONO}>
        <Stack direction="row" spacing={1} alignItems="center">
          {produced
            ? <CheckCircle fontSize="inherit" color="success" />
            : <RemoveCircleOutline fontSize="inherit" sx={{ color: 'text.disabled' }} />}
          <Typography sx={{ ...MONO, color: produced ? 'success.main' : 'text.secondary' }}>
            {`+${ev.atMs}ms  déterministe · ${ev.provider} → ${produced ? 'contenu produit' : 'rien'}`}
          </Typography>
        </Stack>
        {produced && <JsonBlock value={ev.content} label="contenu déterministe" />}
      </Box>
    );
  }

  if (ev.stage === 'llm') {
    const gloss = ev.purpose === 'generation'
      ? 'génération'
      : ev.purpose === 'correction'
        ? 'vérification / correctif sémantique'
        : ev.purpose;
    return (
      <Box sx={MONO}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesome fontSize="inherit" color="secondary" />
          <Typography sx={{ ...MONO, color: 'secondary.main' }}>
            {`+${ev.atMs}ms  LLM · ${gloss}${ev.temperature != null ? ` (temp ${ev.temperature})` : ''}`}
          </Typography>
        </Stack>
        <JsonBlock value={ev.output} label="sortie LLM" />
      </Box>
    );
  }

  return (
    <Typography sx={{ ...MONO, color: 'text.secondary' }}>
      {`+${ev.atMs}ms  ${JSON.stringify(ev)}`}
    </Typography>
  );
}

function DefectList({ title, defects }) {
  if (!defects || defects.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">{title} ({defects.length})</Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {defects.map((d, i) => (
          <li key={i}>
            <Typography sx={MONO}>
              <strong>[{d.type || d.kind || '?'}{d.severity ? ` · ${d.severity}` : ''}]</strong>
              {d.itemIndex != null ? ` #${d.itemIndex}` : ''} {d.description || ''}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );
}

const GATE_STATUS_COLOR = {
  valid: 'success',
  repaired: 'info',
  semantic_issues: 'warning',
  invalid: 'error',
};

export default function GenerationLogPanel({ trace }) {
  const [open, setOpen] = useState(false);
  if (!trace) return null;

  const { events = [], rawContent, gate } = trace;
  const detCount = gate?.deterministic?.length || 0;
  const semCount = gate?.semantic?.length || 0;

  return (
    <Paper variant="outlined" sx={{ mt: 3 }}>
      <Box
        sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Terminal fontSize="small" />
          <Typography variant="subtitle2">Journal de génération (debug)</Typography>
          {gate?.status && (
            <Chip size="small" color={GATE_STATUS_COLOR[gate.status] || 'default'} label={gate.status} />
          )}
          <Chip size="small" variant="outlined" icon={<Bolt fontSize="inherit" />} label={`${events.length} étapes`} />
          {detCount > 0 && <Chip size="small" color="warning" variant="outlined" label={`dét. ${detCount}`} />}
          {semCount > 0 && <Chip size="small" color="warning" variant="outlined" label={`sém. ${semCount}`} />}
        </Stack>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Box>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Étapes (chronologique)</Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {events.length === 0 ? (
              <Typography sx={{ ...MONO, color: 'text.disabled' }}>
                Aucune étape tracée (contenu probablement servi depuis le cache / hors pipeline).
              </Typography>
            ) : (
              events.map((ev, i) => <EventRow key={i} ev={ev} />)
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">Vérification &amp; gates</Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              color={GATE_STATUS_COLOR[gate?.status] || 'default'}
              label={`statut : ${gate?.status || '—'}`}
            />
            {gate?.proposed && (
              <Chip size="small" variant="outlined" sx={{ ml: 1 }} label="correctif sémantique proposé" />
            )}
            {detCount === 0 && semCount === 0 && (
              <Typography sx={{ ...MONO, color: 'success.main', mt: 1 }}>
                Aucun défaut résiduel.
              </Typography>
            )}
            <DefectList title="Défauts déterministes (après réparation)" defects={gate?.deterministic} />
            <DefectList title="Défauts sémantiques (juge LLM)" defects={gate?.semantic} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <JsonBlock value={rawContent} label="Contenu brut (avant gate)" />
        </Box>
      </Collapse>
    </Paper>
  );
}
